import type { ShapeName } from "@/lib/grid-logic"

export type SkillItemArtShape = Extract<
  ShapeName,
  "1x1" | "1x2" | "2x1" | "1x3" | "3x1"
>

export type SkillItemArtRecord = {
  assetKey: string
  collectionKey: string | null
  conceptKey: string
  name: string
  category: string
  theme: string | null
  shape: SkillItemArtShape
  orientation: "square" | "portrait" | "landscape"
  prompt: string
  provider: "nano-banana"
  status: "ready"
  referenceImageUrl?: string | null
  imageUrl: string
  pixelWidth: number
  pixelHeight: number
  createdAt: string | null
}

type GenerateSkillIconInput = {
  slug: string
  name: string
  description: string | null | undefined
  category: string | null | undefined
  shape: SkillItemArtShape
}

type SkillItemArtTier = "small" | "medium" | "large"

const DEFAULT_COLLECTION_KEYS = new Set(["ocean-pack-2026-04-15"])
const EDITOR_COLLECTION_KEY = "skill-editor-icons"
// Clothing assets are drawn as frontal garments and look broken when
// rendered at 2x1 / 3x1 aspect ratios. Exclude them from auto-resolution.
const EXCLUDED_ASSET_CATEGORIES = new Set(["clothing"])

function isAssetCategoryAllowed(record: SkillItemArtRecord): boolean {
  return !EXCLUDED_ASSET_CATEGORIES.has(record.category.toLowerCase())
}

let artCache: SkillItemArtRecord[] | null = null
let artPromise: Promise<SkillItemArtRecord[]> | null = null
let artCacheExpiresAt = 0
const ART_CACHE_TTL_MS = 10_000

type SkillItemArtApiRecord =
  | SkillItemArtRecord
  | {
      asset_key?: string
      collection_key?: string | null
      concept_key?: string
      name?: string
      category?: string
      theme?: string | null
      shape?: SkillItemArtShape
      orientation?: "square" | "portrait" | "landscape"
      prompt?: string
      provider?: "nano-banana"
      status?: "ready"
      reference_image_url?: string | null
      image_url?: string
      pixel_width?: number
      pixel_height?: number
      created_at?: string | null
    }

function normalizeSkillItemArtRecord(
  record: SkillItemArtApiRecord
): SkillItemArtRecord | null {
  const assetKey = "assetKey" in record ? record.assetKey : record.asset_key
  const collectionKey =
    "collectionKey" in record ? record.collectionKey : record.collection_key
  const conceptKey =
    "conceptKey" in record ? record.conceptKey : record.concept_key
  const imageUrl = "imageUrl" in record ? record.imageUrl : record.image_url
  const pixelWidth =
    "pixelWidth" in record ? record.pixelWidth : record.pixel_width
  const pixelHeight =
    "pixelHeight" in record ? record.pixelHeight : record.pixel_height
  const createdAt = "createdAt" in record ? record.createdAt : record.created_at
  const referenceImageUrl =
    "referenceImageUrl" in record
      ? record.referenceImageUrl
      : "reference_image_url" in record
        ? record.reference_image_url
        : null

  if (
    !assetKey ||
    !conceptKey ||
    !imageUrl ||
    !record.name ||
    !record.category ||
    !record.shape ||
    !record.orientation ||
    !record.prompt ||
    !record.provider ||
    !record.status ||
    typeof pixelWidth !== "number" ||
    typeof pixelHeight !== "number"
  ) {
    return null
  }

  return {
    assetKey,
    collectionKey: collectionKey ?? null,
    conceptKey,
    name: record.name,
    category: record.category,
    theme: record.theme ?? null,
    shape: record.shape,
    orientation: record.orientation,
    prompt: record.prompt,
    provider: record.provider,
    status: record.status,
    referenceImageUrl: referenceImageUrl ?? null,
    imageUrl,
    pixelWidth,
    pixelHeight,
    createdAt: createdAt ?? null,
  }
}

function normalizeSkillItemArtRecords(
  records: SkillItemArtApiRecord[] | undefined
): SkillItemArtRecord[] {
  return (records ?? [])
    .map((record) => normalizeSkillItemArtRecord(record))
    .filter((record): record is SkillItemArtRecord => Boolean(record))
}

function stableHash(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function toSkillConceptKey(slug: string): string {
  return `skill-${slug}`.replace(/[^a-z0-9-]/gi, "-").toLowerCase()
}

function normalizeSkillCategory(category?: string | null): string | null {
  const normalized = category
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return normalized || null
}

function preferredCategoryForShape(shape: SkillItemArtShape): string {
  if (shape === "1x1") return "item"
  // Medium and large tiers both prefer "weapon" so assets read well at 2x1
  // or 3x1 horizontal aspect (and when rotated 90°). Clothing is excluded
  // from auto-resolution because it's drawn as a frontal garment.
  return "weapon"
}

function artTierForSkillMdSize(skillMdSize?: number | null): SkillItemArtTier {
  if ((skillMdSize ?? 0) < 2048) return "small"
  if ((skillMdSize ?? 0) <= 8192) return "medium"
  return "large"
}

function canonicalHorizontalShapeForTier(
  tier: SkillItemArtTier
): SkillItemArtShape {
  if (tier === "small") return "1x1"
  if (tier === "medium") return "2x1"
  return "3x1"
}

export function canonicalHorizontalShapeForSkillMdSize(
  skillMdSize?: number | null
): SkillItemArtShape {
  return canonicalHorizontalShapeForTier(artTierForSkillMdSize(skillMdSize))
}

export function isVerticalShape(shape: SkillItemArtShape): boolean {
  return shape === "1x2" || shape === "1x3"
}

function buildSkillPrompt({
  name,
  description,
  category,
}: Pick<GenerateSkillIconInput, "name" | "description" | "category">): string {
  const normalizedDescription = description?.trim()
  const normalizedCategory = category?.trim()
  return [
    `Create a symbolic fantasy skill icon for "${name}".`,
    normalizedDescription
      ? `Represent this skill idea: ${normalizedDescription}.`
      : null,
    normalizedCategory ? `The skill category is ${normalizedCategory}.` : null,
    "Use a single readable object, tool, relic, weapon, garment, trinket, or artifact rather than text or interface screenshots.",
    "Favor strong silhouette, high readability at small size, and visually distinct materials that communicate the skill at a glance.",
  ]
    .filter(Boolean)
    .join(" ")
}

function selectDeterministicRecord(
  records: SkillItemArtRecord[],
  seed: string
): SkillItemArtRecord | null {
  if (records.length === 0) return null
  return records[stableHash(seed) % records.length] ?? null
}

export async function fetchSkillItemArtRecords(
  force = false
): Promise<SkillItemArtRecord[]> {
  if (!force && artCache && Date.now() < artCacheExpiresAt) return artCache
  if (!force && artPromise) return artPromise

  artPromise = fetch("/api/skill-item-art", {
    method: "GET",
    cache: "no-store",
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch skill item art (${response.status})`)
      }
      const body = (await response.json()) as { items?: SkillItemArtApiRecord[] }
      artCache = normalizeSkillItemArtRecords(body.items)
      artCacheExpiresAt = Date.now() + ART_CACHE_TTL_MS
      return artCache
    })
    .finally(() => {
      artPromise = null
    })

  return artPromise
}

export function mergeSkillItemArtRecords(
  current: SkillItemArtRecord[],
  updates: SkillItemArtRecord[]
): SkillItemArtRecord[] {
  const byAssetKey = new Map(current.map((record) => [record.assetKey, record]))
  for (const record of updates) {
    byAssetKey.set(record.assetKey, record)
  }
  const merged = Array.from(byAssetKey.values())
  artCache = merged
  artCacheExpiresAt = Date.now() + ART_CACHE_TTL_MS
  return merged
}

/**
 * Resolve the canonical art record for a skill.
 *
 * One asset per skill across the whole app. The asset is always the
 * horizontal-shape record for the skill's size tier (1x1 / 2x1 / 3x1).
 * Vertical display (1x2 / 1x3) is produced by rotating this asset 90° at
 * render time, not by a separate record.
 */
export function resolveSkillIcon({
  skillId,
  category,
  skillMdSize,
  records,
}: {
  skillId: string
  category?: string | null
  skillMdSize?: number | null
  records: SkillItemArtRecord[]
}): SkillItemArtRecord | null {
  const conceptKey = toSkillConceptKey(skillId)
  const categoryKey = normalizeSkillCategory(category)
  const horizontalShape = canonicalHorizontalShapeForSkillMdSize(skillMdSize)
  const artSeed = categoryKey
    ? `category:${categoryKey}:shape:${horizontalShape}`
    : `skill:${skillId}:shape:${horizontalShape}`

  // Explicit (editor-picked) records are always honored — the user knowingly
  // assigned that asset even if it's in an otherwise-excluded category.
  const explicit = records.find(
    (record) =>
      record.collectionKey === EDITOR_COLLECTION_KEY &&
      record.conceptKey === conceptKey &&
      record.shape === horizontalShape
  )
  if (explicit) return explicit

  const defaultPool = records.filter(
    (record) =>
      DEFAULT_COLLECTION_KEYS.has(record.collectionKey ?? "") &&
      record.shape === horizontalShape &&
      isAssetCategoryAllowed(record)
  )
  if (defaultPool.length > 0) {
    return selectDeterministicRecord(defaultPool, artSeed)
  }

  const fallbackPool = records.filter(
    (record) =>
      record.shape === horizontalShape &&
      record.conceptKey !== conceptKey &&
      isAssetCategoryAllowed(record)
  )
  return selectDeterministicRecord(fallbackPool, `${artSeed}:fallback`)
}

/** @deprecated use resolveSkillIcon instead — preserved for migration. */
export function resolveSkillIconForShape(args: {
  skillId: string
  category?: string | null
  shape: SkillItemArtShape
  records: SkillItemArtRecord[]
}): SkillItemArtRecord | null {
  return resolveSkillIcon({
    skillId: args.skillId,
    category: args.category,
    skillMdSize: null,
    records: args.records,
  })
}

/** @deprecated use resolveSkillIcon instead — preserved for migration. */
export function resolveSkillIconForSkillMdSize(args: {
  skillId: string
  category?: string | null
  skillMdSize?: number | null
  records: SkillItemArtRecord[]
}): SkillItemArtRecord | null {
  return resolveSkillIcon(args)
}

export async function generateSkillIcon(
  input: GenerateSkillIconInput
): Promise<SkillItemArtRecord[]> {
  const response = await fetch("/api/skill-item-art/generate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      collectionKey: EDITOR_COLLECTION_KEY,
      theme:
        "Symbolic fantasy skill artifacts, arcane tools, readable silhouettes, and polished RPG iconography",
      items: [
        {
          conceptKey: toSkillConceptKey(input.slug),
          name: input.name,
          category: preferredCategoryForShape(input.shape),
          prompt: buildSkillPrompt(input),
          shapeVariants: [input.shape],
        },
      ],
    }),
  })

  const body = (await response.json()) as {
    items?: SkillItemArtApiRecord[]
    detail?: string
  }
  if (!response.ok) {
    throw new Error(body.detail ?? `Failed to generate icon (${response.status})`)
  }

  const updates = normalizeSkillItemArtRecords(body.items)
  mergeSkillItemArtRecords(artCache ?? [], updates)
  return updates
}
